import { logoIllustrationClassName, logoImageClassName } from '@/pages/onboarding/onboarding.styles';
import type {
  IllustrationBarProps,
  NoteIllustrationProps,
  OnboardingIllustrationProps,
  RecordingIllustrationProps,
} from '@/pages/onboarding/onboarding.types';
import type { NoteDetail } from '@/types/noteStyle';
import { Box, Icon, Image, Skeleton, type SkeletonProps, Text } from '@careflux/primitives';

/** Abstract placeholder "text" line. */
const Bar = ({ tone, width }: IllustrationBarProps) => (
  <Skeleton animated={false} tone={tone} width={width} height="2" rounded="full" />
);

const LogoIllustration = () => (
  <Box
    display="flex"
    align="center"
    justify="center"
    rounded="full"
    background="card"
    shadow="2xl"
    className={logoIllustrationClassName}
  >
    <Image src="/careflux-small.svg" alt="" objectFit="contain" className={logoImageClassName} />
  </Box>
);

// Fixed pseudo-random bar heights so the waveform is stable between renders.
const WAVE_PATTERN: SkeletonProps['height'][] = ['3', '5', '7', '4', '8', '6', '9'];
const WAVE_HEIGHTS = Array.from({ length: 28 }, (_, index) => WAVE_PATTERN[(index * 3) % WAVE_PATTERN.length]);
const WAVE_PLAYED = 18;

const RecordingIllustration = ({ modeLabel }: RecordingIllustrationProps) => (
  <Box display="flex" direction="col" gap="lg" background="card" shadow="2xl" rounded="xl" padding="lg" width="80">
    <Box display="flex" align="center" justify="between">
      <Box display="flex" align="center" gap="sm">
        <Box width="2" height="2" rounded="full" background="destructive" />
        <Text size="xs" weight="medium">
          REC • 04:12
        </Text>
      </Box>
      <Box rounded="full" background="accent" paddingX="sm" paddingY="xs">
        <Text size="xs" weight="medium" color="primary">
          {modeLabel}
        </Text>
      </Box>
    </Box>
    <Box display="flex" align="center" gap="xs" height="10">
      {WAVE_HEIGHTS.map((height, index) =>
        index < WAVE_PLAYED ? (
          // biome-ignore lint/suspicious/noArrayIndexKey: static decorative bars
          <Box key={index} width="1" height={height} rounded="full" background="primary" />
        ) : (
          // biome-ignore lint/suspicious/noArrayIndexKey: static decorative bars
          <Skeleton key={index} animated={false} tone="accent" width="1" height={height} rounded="full" />
        )
      )}
    </Box>
    <Box display="flex" direction="col" gap="sm">
      <Bar tone="muted" width="full" />
      <Bar tone="muted" width="60" />
    </Box>
  </Box>
);

const NOTE_LINES: Record<NoteDetail, number> = { short: 4, balanced: 7, detailed: 11 };
const LINE_WIDTHS: IllustrationBarProps['width'][] = ['full', '64', '56', '60', '44'];

const NoteIllustration = ({ detail }: NoteIllustrationProps) => (
  <Box display="flex" direction="col" gap="sm" background="card" shadow="2xl" rounded="xl" padding="lg" width="80">
    <Box marginBottom="xs">
      <Bar tone="strong" width="32" />
    </Box>
    {Array.from({ length: NOTE_LINES[detail] }, (_, index) => (
      <Bar
        // biome-ignore lint/suspicious/noArrayIndexKey: static decorative lines
        key={index}
        tone={index % 4 === 0 ? 'strong' : 'muted'}
        width={LINE_WIDTHS[index % LINE_WIDTHS.length]}
      />
    ))}
  </Box>
);

const CompleteIllustration = () => (
  <Box
    display="flex"
    direction="col"
    align="center"
    gap="lg"
    background="card"
    shadow="2xl"
    rounded="xl"
    paddingX="lg"
    paddingY="2xl"
    width="72"
  >
    <Box display="flex" align="center" justify="center" rounded="full" background="primary" width="16" height="16">
      <Icon name="Check" size="xl" color="primary-foreground" />
    </Box>
    <Box display="flex" direction="col" align="center" gap="sm" width="full">
      <Bar tone="strong" width="32" />
      <Bar tone="muted" width="52" />
    </Box>
  </Box>
);

export const OnboardingIllustration = ({ step, values, copy }: OnboardingIllustrationProps) => {
  switch (step) {
    case 1:
      return <LogoIllustration />;
    case 2:
      return <RecordingIllustration modeLabel={copy.work.modes[values.sessionMode ?? 'conversation'].label} />;
    case 3:
      return <NoteIllustration detail={values.noteDetail} />;
    default:
      return <CompleteIllustration />;
  }
};
